
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { fromEvent, Observable, of, Subject } from 'rxjs';
import { map, mapTo, switchMap, tap } from 'rxjs/operators';
import { NotificationService } from '@aston/foundation';
import { AppConstants } from 'apps/console/src/app/app.constants';

import { IFileUploadConfig } from '../models';

@Injectable({
	providedIn: 'root'
})
export class FileUploadService {

	private notificationService = inject(NotificationService)
	private http = inject(HttpClient)

	getFileFromUri(uri: string): Observable<{ file: File; uri: string; }> {
		return this.http.get(uri, { responseType: 'blob' }).pipe(
			switchMap(fileBlob => {
				return this.readFile((fileBlob as File))
					.pipe(map(fileUri => ({ file: fileBlob as File, uri: fileUri })));
			})
		);
	}

	checkFile(file: File, config: IFileUploadConfig): Observable<boolean> {
		// Size check
		if (file.size > config.maxSize) {
			this.notificationService.errorMessage('File.Size');
			return of(false);
		}

		// File name check
		if (file.name.length >= AppConstants.FILE_NAME_MAX_LENGTH) {
			this.notificationService.errorMessage('File.NameLength');
			return of(false);
		}

		// Extension check
		if (!config.acceptExtensions.some(ext => file.type.includes(ext))) {
			this.notificationService.errorMessage('File.Format');
			return of(false);
		}

		// Dimensions check
		if (config.maxResolution) {
			return this.checkImageDimensions(file, config).pipe(
				tap(ok => { if (!ok) this.notificationService.errorMessage('File.Dimensions') }
				));
		}

		return of(true);
	}

	checkImageDimensions(file: File, config: IFileUploadConfig): Observable<boolean> {
		return this.readFile(file).pipe(
			switchMap(url => {
				const image = new Image();
				image.src = url;
				return fromEvent(image, 'load').pipe(mapTo(image));
			}),
			map(image => image.width <= config.maxResolution.width && image.height <= config.maxResolution.height),
		)
	}

	readFile(file: File): Observable<string> {
		const sub = new Subject<string>();
		const reader = new FileReader();

		reader.readAsDataURL(file);
		reader.onload = (e: any) => {
			sub.next(e.target.result);
			sub.complete();
		};

		return sub.asObservable();
	}
}
